import { FC } from 'react';
import {
  ButtonGroup,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
} from '@chakra-ui/react';
import { BiDotsVerticalRounded } from 'react-icons/bi';

import { FileDetailsFileFragment } from '../../../graphql/generated';
import { useRestFileDownload } from '../../../hooks/use-rest-file-download';
import { FileDownloadButton } from '../../file-download-button/file-download-button';
import { FileProcessesRetry } from '../../file-processes-retry/file-processes-retry';
import { FileArchiveAction } from './file-archive-action/file-archive-action';
import { FileDeleteAction } from './file-delete-action/file-delete-action';
import { FileMoveAction } from './file-move-action/file-move-action';
import { FileRenameAction } from './file-rename-action/file-rename-action';
import { FileValidationAction } from './file-validation-action/file-validation-action';

type Props = {
  file: FileDetailsFileFragment;
};

export const FileDetailsActions: FC<Props> = ({ file }) => {
  const { downloadFile } = useRestFileDownload();

  const handleDownload = async () => {
    try {
      await downloadFile({
        cedantName: file.cedant,
        id: file.id,
        actualFileName: file.name,
      });
    } catch (error) {
      console.error('File download failed:', error);
    }
  };

  const {
    canRename,
    canMove,
    canArchive,
    canDelete,
    canRetryProcesses,
  } = file;

  return (
    <>
      <HStack spacing="8">
        <ButtonGroup size="lg" isAttached variant="solid">
          <FileDownloadButton file={file} onDownload={handleDownload} />
          <Menu>
            <MenuButton
              as={IconButton}
              data-testid="more-file-actions"
              icon={<BiDotsVerticalRounded />}
            />
            <MenuList>
              {canRename && <FileRenameAction file={file} />}
              {canMove && <FileMoveAction files={[file]} buttonType="MenuItem" />}
              {canArchive && <FileArchiveAction file={file} />}
              {canDelete && <FileDeleteAction file={file} />}
              {(canRename || canArchive || canDelete) && <MenuDivider />}
              <FileValidationAction hash={undefined} />
            </MenuList>
          </Menu>
        </ButtonGroup>
        {canRetryProcesses && <FileProcessesRetry fileId={file.id} />}
      </HStack>
    </>
  );
};
